import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, ListGroup, Button, Form, Dropdown, Spinner, Modal, Offcanvas } from 'react-bootstrap';
import { PlusCircle, MessageSquare, LogOut, Trash2, Moon, Sun, Mic, Settings, Send, UserX, HelpCircle, Code, History, Menu, ChevronDown } from 'lucide-react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';

const Dashboard = () => {
    // --- States ---
    const [history, setHistory] = useState([]);
    const [code, setCode] = useState('');
    const [review, setReview] = useState('');
    const [loading, setLoading] = useState(false);
    const [currentChatId, setCurrentChatId] = useState(null);
    const [isRecording, setIsRecording] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [showHelp, setShowHelp] = useState(false); 
    const [showMobileMenu, setShowMobileMenu] = useState(false);
    const [newName, setNewName] = useState("");
    const [showScrollBtn, setShowScrollBtn] = useState(false);
    
    const [userName, setUserName] = useState(localStorage.getItem('userName') || "Kuldeep Kumawat");
    const [theme, setTheme] = useState(localStorage.getItem('appTheme') || 'dark');
    
    // Refs for scrolling and auto-resize
    const scrollContainerRef = useRef(null);
    const reviewEndRef = useRef(null);
    const textareaRef = useRef(null);

    const API_URL = process.env.REACT_APP_API_URL;

    // --- Functions ---
    const fetchHistory = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_URL}/history`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setHistory(res.data);
        } catch (err) { console.error("History fetch error"); }
    }, [API_URL]);

    const loadChat = useCallback(async (chatId) => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_URL}/chat/${chatId}`, { 
                headers: { Authorization: `Bearer ${token}` } 
            });
            setCurrentChatId(chatId);
            setCode(res.data.user_code);
            setReview(res.data.ai_review);
        } catch (err) { alert("Chat load failed!"); }
        setLoading(false);
    }, [API_URL]);

    // Scroll Detection
    const handleScroll = () => {
        if (scrollContainerRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
            setShowScrollBtn(scrollHeight - scrollTop > clientHeight + 150);
        }
    };

    const scrollToBottom = () => {
        reviewEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    // Auto-resize Textarea
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
            textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 250)}px`;
        }
    }, [code]);

    useEffect(() => {
        fetchHistory();
        document.documentElement.setAttribute('data-bs-theme', theme);
        localStorage.setItem('appTheme', theme);
    }, [theme, fetchHistory]);

    // --- Action Handlers ---
    const deleteChat = async (e, id) => {
        e.stopPropagation();
        if (!window.confirm("Delete history?")) return;
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_URL}/chat/${id}`, { headers: { Authorization: `Bearer ${token}` } });
            fetchHistory();
        } catch (err) { alert("Delete failed"); }
    };

    const handleUpdateName = async () => {
        if (!newName.trim()) return;
        try {
            const token = localStorage.getItem('token');
            const res = await axios.put(`${API_URL}/profile/edit-name`, { name: newName }, { headers: { Authorization: `Bearer ${token}` } });
            setUserName(res.data.new_name);
            localStorage.setItem('userName', res.data.new_name);
            setShowSettings(false);
        } catch (err) { alert("Update failed!"); }
    };

    const handleDeleteAccount = async () => {
        if (window.confirm("WARNING: Permanent delete?")) {
            try {
                const token = localStorage.getItem('token');
                await axios.delete(`${API_URL}/profile/delete-account`, { headers: { Authorization: `Bearer ${token}` } });
                localStorage.clear();
                window.location.reload();
            } catch (err) { alert("Delete failed"); }
        }
    };

    const handleReview = async () => {
        if (!code.trim()) return;
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.post(`${API_URL}/review`, { code, language: 'javascript' }, { headers: { Authorization: `Bearer ${token}` } });
            setReview(res.data.review);
            fetchHistory();
            setTimeout(scrollToBottom, 100);
        } catch (err) { alert("Review failed!"); }
        setLoading(false);
    };

    // --- Voice Implementation ---
    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mediaRecorder = new MediaRecorder(stream);
            const audioChunks = [];
            mediaRecorder.ondataavailable = (e) => audioChunks.push(e.data);
            mediaRecorder.onstop = async () => {
                const audioBlob = new Blob(audioChunks, { type: 'audio/wav' });
                const formData = new FormData();
                formData.append('file', audioBlob, 'recording.wav');
                const token = localStorage.getItem('token');
                const res = await axios.post(`${API_URL}/voice-to-text`, formData, {
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                });
                setCode(res.data.text);
                stream.getTracks().forEach(track => track.stop());
            };
            mediaRecorder.start();
            setIsRecording(true);
            setTimeout(() => { mediaRecorder.stop(); setIsRecording(false); }, 5000);
        } catch (err) { alert("Mic error"); }
    };

    const SidebarContent = () => (
        <div className={`d-flex flex-column h-100 p-3 ${theme === 'dark' ? 'bg-black text-white' : 'bg-white text-dark border-end'}`}>
            <Button variant={theme === 'dark' ? "outline-light" : "outline-primary"} className="w-100 mb-4 py-2 fw-bold" onClick={() => { setCode(''); setReview(''); setCurrentChatId(null); setShowMobileMenu(false); }}>
                <PlusCircle size={18} className="me-2"/> New Chat
            </Button>
            
            <div className="flex-grow-1 overflow-auto custom-scrollbar">
                <ListGroup variant="flush">
                    {history.map(item => (
                        <div key={item.id} className={`sidebar-chat-wrapper rounded mb-1 px-2 d-flex align-items-center ${currentChatId === item.id ? 'active-chat' : ''}`}>
                            <div style={{width: 'calc(100% - 30px)'}} className="flex-grow-1 text-truncate py-2 small cursor-pointer" onClick={() => { loadChat(item.id); setShowMobileMenu(false); }}>
                                <MessageSquare size={14} className="me-2 text-info"/> 
                                {item.title}
                            </div>
                            <Trash2 size={13} className="text-danger ms-2 cursor-pointer" onClick={(e) => deleteChat(e, item.id)} />
                        </div>
                    ))}
                </ListGroup>
            </div>

            <div className="mt-auto pt-3 border-top border-secondary">
                <Dropdown drop="up">
                    <Dropdown.Toggle variant="link" className="text-decoration-none p-0 d-flex align-items-center w-100 border-0 shadow-none text-reset">
                        <div className="bg-primary rounded-circle text-white d-flex align-items-center justify-content-center me-2 fw-bold" style={{width: '32px', height: '32px'}}>
                            {userName.charAt(0).toUpperCase()}
                        </div>
                        <span className="small fw-bold flex-grow-1 text-start text-truncate">{userName}</span>
                        <Settings size={16} className="text-secondary" />
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="shadow-lg p-2 border-0">
                        <Dropdown.Item onClick={() => setShowSettings(true)}><Settings size={16} className="me-2"/> Settings</Dropdown.Item>
                        <Dropdown.Item onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
                            {theme === 'dark' ? <Sun size={16} className="me-2 text-warning"/> : <Moon size={16} className="me-2 text-primary"/>}
                            {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => setShowHelp(true)}><HelpCircle size={16} className="me-2 text-info"/> Help & Guide</Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item onClick={handleDeleteAccount} className="text-danger"><UserX size={16} className="me-2"/> Delete Account</Dropdown.Item>
                        <Dropdown.Item className="text-danger" onClick={() => {localStorage.clear(); window.location.reload();}}><LogOut size={16} className="me-2"/> Logout</Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            </div>
        </div>
    );

    return (
        <Container fluid className="vh-100 p-0 d-flex overflow-hidden">
            <div className="d-none d-md-block h-100 shadow-sm" style={{ width: '260px' }}>
                <SidebarContent />
            </div>

            <Offcanvas show={showMobileMenu} onHide={() => setShowMobileMenu(false)} className={theme === 'dark' ? 'bg-black text-white' : 'bg-white'}>
                <Offcanvas.Header closeButton className={theme === 'dark' ? 'btn-close-white' : ''}><Offcanvas.Title className="fw-bold">History</Offcanvas.Title></Offcanvas.Header>
                <Offcanvas.Body className="p-0"><SidebarContent /></Offcanvas.Body>
            </Offcanvas>

            <div className={`flex-grow-1 h-100 w-100 d-flex flex-column position-relative ${theme === 'dark' ? 'bg-dark text-white' : 'bg-white text-dark'}`}>
                <div className="d-md-none p-3 border-bottom d-flex align-items-center justify-content-between">
                    <Button variant="link" className="p-0 text-reset shadow-none" onClick={() => setShowMobileMenu(true)}><Menu size={24} /></Button>
                    <span className="fw-bold small">AI Code Reviewer</span>
                    <PlusCircle size={20} className="text-primary" onClick={() => { setCode(''); setReview(''); setCurrentChatId(null); }} />
                </div>

                <div className="flex-grow-1 overflow-auto custom-scrollbar p-3 p-md-5" ref={scrollContainerRef} onScroll={handleScroll}>
                    <div className="mx-auto" style={{ maxWidth: '800px' }}>
                        {!review && !loading && (
                            <div className="text-center mt-5 pt-5 opacity-50">
                                <h2 className="fw-bold">Hi {userName.split(' ')[0]}, let's review code.</h2>
                            </div>
                        )}
                        {review && (
                            <div className={`p-4 rounded-4 shadow-sm mb-4 ${theme === 'dark' ? 'bg-black border border-secondary' : 'bg-light border'}`}>
                                <ReactMarkdown>{review}</ReactMarkdown>
                                <div ref={reviewEndRef} />
                            </div>
                        )}
                        {loading && <div className="text-center p-5"><Spinner animation="grow" variant="primary" /></div>}
                    </div>
                </div>

                {showScrollBtn && (
                    <Button onClick={scrollToBottom} className="position-absolute rounded-circle shadow p-0 d-flex align-items-center justify-content-center" style={{ bottom: '130px', left: '50%', transform: 'translateX(-50%)', zIndex: 10, width: '35px', height: '35px' }} variant={theme === 'dark' ? "secondary" : "light"}>
                        <ChevronDown size={20} />
                    </Button>
                )}

                <div className="p-3 p-md-4">
                    <div className="mx-auto" style={{ maxWidth: '820px' }}>
                        <div className={`d-flex align-items-end rounded-4 p-2 shadow-sm ${theme === 'dark' ? 'bg-black border border-secondary' : 'bg-light border'}`}>
                            <Button variant="link" className="text-secondary p-2 shadow-none" onClick={startRecording}><Mic size={20} className={isRecording ? "text-danger" : ""} /></Button>
                            <textarea ref={textareaRef} rows={1} className="bg-transparent border-0 shadow-none mx-2 text-reset flex-grow-1 py-2" placeholder="Paste code or ask a question..." style={{ resize: 'none', maxHeight: '250px', outline: 'none' }} value={code} onChange={(e) => setCode(e.target.value)} />
                            <Button variant="primary" className="rounded-circle p-2 d-flex align-items-center justify-content-center shadow-none" onClick={handleReview} disabled={loading || !code.trim()} style={{width: '40px', height: '40px', minWidth: '40px'}}><Send size={18} color="white" /></Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Help Modal with History and Code Icons */}
            <Modal show={showHelp} onHide={() => setShowHelp(false)} size="lg" centered scrollable>
                <Modal.Header closeButton className="border-0"><Modal.Title className="fw-bold text-primary">User Guide</Modal.Title></Modal.Header>
                <Modal.Body className="pt-0">
                    <div className="p-2">
                        <h5 className="fw-bold mb-3 d-flex align-items-center"><Code size={20} className="me-2 text-primary"/> How to use?</h5>
                        <p className="small text-muted ps-4">Paste code in the bar below and click Send.</p>
                        <h5 className="fw-bold mb-3 mt-4 d-flex align-items-center"><History size={20} className="me-2 text-warning"/> Chat History</h5>
                        <p className="small text-muted ps-4">Your past reviews are saved in the sidebar.</p>
                    </div>
                </Modal.Body>
            </Modal>

            <Modal show={showSettings} onHide={() => setShowSettings(false)} centered>
                <Modal.Header closeButton className="border-0 pb-0"><Modal.Title className="fw-bold">Settings</Modal.Title></Modal.Header>
                <Modal.Body className="pt-3">
                    <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold text-muted text-uppercase">Display Name</Form.Label>
                        <Form.Control type="text" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={userName} />
                    </Form.Group>
                    <Button variant="primary" className="w-100 py-2 fw-bold" onClick={handleUpdateName}>Save Changes</Button>
                </Modal.Body>
            </Modal>

            <style>{`
                .sidebar-chat-wrapper:hover { background-color: ${theme === 'dark' ? '#222' : '#f0f0f0'}; }
                .active-chat { background-color: ${theme === 'dark' ? '#333' : '#e9ecef'} !important; border-left: 3px solid #0d6efd; }
                .custom-scrollbar::-webkit-scrollbar { width: 6px; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #555; border-radius: 10px; }
            `}</style>
        </Container>
    );
};

export default Dashboard;